/**
 * `app:startup_ready` — one event per launch, fired when the app is actually
 * USABLE, not merely hydrated (`app:start` fires after Zustand hydration,
 * before restored tabs mount Monaco and introspection kicks off — the
 * "freeze-feel" window had no instrumentation at all).
 *
 * Readiness definition (FROZEN — must stay byte-identical across releases or
 * the baseline comparison is meaningless):
 *   startup route resolved → workspace committed → double requestAnimationFrame
 *   (React commit + first paint done) → requestIdleCallback with a 10s cap
 *   (main thread drew breath; on a frozen startup the cap fires and the real
 *   elapsed time is still reported).
 *
 * durationMs anchor differs per route — routes are separate dashboard rows and
 * must never be cross-compared:
 *   - 'empty' / 'saved_connections': renderer start → ready (no human gate).
 *   - 'session_restore': restore start → ready. Anchoring at renderer start
 *     would fold the user's thinking time in the Sessions dialog into the
 *     metric and drown the signal.
 *   - 'start_fresh': Sessions-dialog decision (Start Fresh / New Session /
 *     dialog dismissed) → ready, for the same reason. Without this route the
 *     no-restore cohort emitted neither a startup row nor any resource
 *     samples — a silent population bias in both baselines.
 *
 * Firing also schedules the three `app:resource_sample` requests
 * (startup_ready / 10m / 30m) — main reads Electron process metrics.
 */

import { useWorkspaceStore } from '../../features/workspace';
import { useConnectionStore } from '../../features/connections/store/connectionStore';
import { bucketCount } from './telemetryBuckets';

export type StartupRoute = 'empty' | 'saved_connections' | 'session_restore' | 'start_fresh';

let fired = false;
let decisionAnchorMs: number | null = null;

/** Pure duration selection — see the anchor rules in the module doc. */
export function computeStartupDurationMs(
  route: StartupRoute,
  nowMs: number,
  anchorMs: number | null,
): number {
  if ((route === 'session_restore' || route === 'start_fresh') && anchorMs !== null) {
    return Math.max(0, Math.round(nowMs - anchorMs));
  }
  return Math.max(0, Math.round(nowMs));
}

/**
 * Call at the user's Sessions-dialog decision (restore chosen, start fresh,
 * new session, or dialog dismissed) — anchors durationMs so human thinking
 * time in the dialog stays out of the metric. Idempotent (first call wins).
 */
export function markStartupDecision(): void {
  if (decisionAnchorMs === null) {
    decisionAnchorMs = performance.now();
  }
}

/** Call immediately before the startup session restore begins. Idempotent. */
export function markRestoreStarted(): void {
  markStartupDecision();
}

/**
 * Report the app as ready via the given startup route. Fires at most once per
 * window; later calls (e.g. Pro-mode session switches) are no-ops.
 */
export function reportStartupReady(route: StartupRoute): void {
  if (fired) return;
  fired = true;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const emit = () => {
        const durationMs = computeStartupDurationMs(route, performance.now(), decisionAnchorMs);
        const tabs = useWorkspaceStore.getState().tabs;
        const connections = useConnectionStore.getState().connections;
        window.electronAPI?.analytics
          ?.track('app:startup_ready', {
            route,
            durationMs,
            tabsBucket: bucketCount(tabs.length),
            queryTabsBucket: bucketCount(tabs.filter((t) => t.type === 'query').length),
            connectionsBucket: bucketCount(connections.length),
          })
          .catch(() => {});
        scheduleResourceSamples();
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => emit(), { timeout: 10_000 });
      } else {
        setTimeout(emit, 0);
      }
    });
  });
}

function scheduleResourceSamples(): void {
  const sample = (phase: 'startup_ready' | '10m' | '30m') => {
    const tabCount = useWorkspaceStore.getState().tabs.length;
    window.electronAPI?.analytics?.resourceSample?.(phase, tabCount).catch(() => {});
  };
  sample('startup_ready');
  setTimeout(() => sample('10m'), 10 * 60 * 1000);
  setTimeout(() => sample('30m'), 30 * 60 * 1000);
}

/** Test-only: reset the once-per-window state. */
export function _resetStartupReadyForTests(): void {
  fired = false;
  decisionAnchorMs = null;
}
